/**
 * API Endpoints Configuration
 * Centralized definitions of all API routes used in the application
 */

export const API_ENDPOINTS = {
  // User/Authentication
  USER: {
    LIST: '/api/User',
    GET_BY_EMAIL: (email) => `/api/User/${encodeURIComponent(email)}`,
    REGISTER: '/api/User',
    UPDATE: '/api/User',
    LOGIN: '/api/User/login',
    CONFIRM_EMAIL: '/api/User/confirm-email',
    RESEND_CONFIRMATION: '/api/User/resend-confirmation',
    FORGOT_PASSWORD: '/api/User/forgot-password',
    VALIDATE_RESET_TOKEN: '/api/User/validate-reset-token',
    RESET_PASSWORD: '/api/User/reset-password',
    CHANGE_PASSWORD: '/api/User/change-password',
    SET_ROLE: (id) => `/api/User/${id}/role`,
    DELETE: (id) => `/api/User/${id}`,
  },

  // Patient Management
  PATIENT: {
    LIST: '/api/Patient',
    GET: (id) => `/api/Patient/${id}`,
    CREATE: '/api/Patient/create',
    UPDATE: '/api/Patient',
    DELETE: (id) => `/api/Patient/${id}`,
    GET_ALERTS: (id) => `/api/patients/${id}/alerts`,
    GET_LMBA_HISTORY: (id) => `/api/patients/${id}/lmba-history`,
  },

  // Cases
  CASE: {
    LIST: '/api/Case',
    GET: (id) => `/api/Case/${id}`,
    GET_BY_PATIENT: (patientId) => `/api/Case/patient/${patientId}`,
    CREATE: '/api/Case',
    UPDATE: '/api/Case',
    DELETE: (id) => `/api/Case/${id}`,
    UPDATE_STATUS: (id) => `/api/Case/${id}/status`,
    SET_RUNNING_THEORY: (id) => `/api/Case/${id}/running-theory`,
    CONFIRM_DIAGNOSIS: (id) => `/api/Case/${id}/confirm-diagnosis`,
  },

  // LLM / Medical Analysis
  LLM: {
    CREATE_ANALYSIS: '/llm/create',
    ANALYSE_IMAGE: '/llm/analyse-image',
    EXTRACT_TRANSCRIPT: '/llm/extract-transcript',
  },

  // Analysis Results
  ANALYSIS: {
    GET_RESPONSES: '/api/Analysis/responses',
    GET_RESULTS: '/api/Analysis/results',
    GET_RESULTS_BY_ANALYSIS: (analysisId) => `/api/Analysis/results/by-analysis/${analysisId}`,
  },

  // Differential Diagnosis
  DIAGNOSIS: {
    LIST: '/api/DifferentialDiagnosis',
  },

  // Symptoms
  SYMPTOM: {
    LIST: '/api/Symptom',
  },

  // Audit Log (Admin only)
  AUDIT: {
    LIST: '/api/AuditLog',
    ACTIONS: '/api/AuditLog/actions',
  },

  // Data Export
  EXPORT: {
    PATIENTS_CSV: '/api/Export/patients.csv',
    CASES_CSV: '/api/Export/cases.csv',
    CASE_REPORT: (id) => `/api/Export/case/${id}/report`,
    AUDIT_CSV: '/api/Export/audit.csv',
  },

  // Organisation / Multi-tenancy
  ORGANISATION: {
    ME: '/api/Organisation/me',
    CREATE: '/api/Organisation',
    UPDATE: (id) => `/api/Organisation/${id}`,
    ADD_MEMBER: (id) => `/api/Organisation/${id}/members`,
    REMOVE_MEMBER: (orgId, userId) => `/api/Organisation/${orgId}/members/${userId}`,
  },

  // Questionnaires (ADR-005)
  QUESTIONNAIRE: {
    GET: (id) => `/api/questionnaires/${id}`,
    CREATE_RESPONSE: (id) => `/api/questionnaires/${id}/responses`,
    GET_RESPONSE: (id) => `/api/questionnaires/responses/${id}`,
    UPDATE_RESPONSE: (id) => `/api/questionnaires/responses/${id}`,
    COMPLETE_RESPONSE: (id) => `/api/questionnaires/responses/${id}/complete`,
    SCORE: (id) => `/api/questionnaires/${id}/score`,
  },

  // Alerts (Phase 1)
  ALERTS: {
    GET: (patientId) => `/api/patients/${patientId}/alerts`,
    MARK_REVIEWED: (alertId) => `/api/alerts/${alertId}/review`,
    REFRESH: (patientId) => `/api/alerts/patients/${patientId}/refresh`,
  },

  // Care Plans (ADR-002)
  PLANS: {
    GENERATE: '/api/plans/generate',
    STATUS: (jobId) => `/api/plans/generation-status/${jobId}`,
    GET: (id) => `/api/plans/${id}`,
    REGENERATE_SECTION: (planId, sectionKey) => `/api/plans/${planId}/sections/${sectionKey}/regenerate`,
    ACTIVATE: (id) => `/api/plans/${id}/activate`,
  },

  // Care Plan (Phase 1)
  CARE_PLAN: {
    REGENERATE_SECTION: '/api/care-plan/regenerate-section',
  },

  // Dashboard (Phase 1)
  DASHBOARD: {
    SUMMARY: '/api/dashboard/summary',
    RECENT_CASES: '/api/dashboard/recent-cases',
    ALERTS_SUMMARY: '/api/dashboard/alerts-summary',
    PLANS_SUMMARY: '/api/dashboard/plans-summary',
  },

  // Global Search
  SEARCH: {
    GLOBAL: '/api/Search',
  },

  // FHIR Resources
  CONDITION: {
    CREATE: '/api/conditions',
    GET: (id) => `/api/conditions/${id}`,
  },

  OBSERVATION: {
    CREATE: '/api/observations',
    GET: (id) => `/api/observations/${id}`,
  },

  MEDICATION_REQUEST: {
    CREATE: '/api/medication-requests',
    GET: (id) => `/api/medication-requests/${id}`,
  },
};

export default API_ENDPOINTS;
